import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetRewardsInventory, useUpsertRewardsInventory } from '~/hooks/apis/rewards/useRewardsInventory';

import type { RewardsTier } from '~/services/RewardsService';

/** Tier display order and labels. */
const TIERS: Array<{ key: RewardsTier; label: string }> = [
  { key: 'bronze', label: 'Bronze' },
  { key: 'silver', label: 'Silver' },
  { key: 'gold', label: 'Gold' },
  { key: 'platinum', label: 'Platinum' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  entryId: string | null;
  entryLabel: string;
}

/**
 * Dialog for configuring inventory quantities per tier for a reward entry.
 *
 * Shows current inventory status (total/reserved/redeemed/available) and
 * allows editing totalQuantity per tier. Read-only fields show live reservation
 * and redemption counts for operational visibility.
 */
export default function ClassicRewardsInventoryDialog({
  open,
  onClose,
  entryId,
  entryLabel,
}: Props): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { data: inventory, refetch, isFetching: isInventoryFetching } = useGetRewardsInventory(open ? entryId : null);
  const upsertInventory = useUpsertRewardsInventory();

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  /**
   * Tracks whether the local `quantities` map reflects the inventory for the
   * currently selected `entryId`. Reset to false when `entryId` changes so the
   * Save button is disabled until fresh inventory arrives — this prevents
   * upserting stale (or default-zero) quantities to a newly selected entry.
   */
  const [isQuantitiesSynced, setIsQuantitiesSynced] = useState(false);

  /** Refetch inventory every time the dialog opens to get the latest numbers. */
  useEffect(() => {
    if (open && entryId) {
      refetch();
    }
  }, [open, entryId, refetch]);

  /** Reset local state on entry change so we don't carry stale values across entries. */
  useEffect(() => {
    setQuantities({});
    setIsQuantitiesSynced(false);
  }, [entryId]);

  /** Sync local state from fetched inventory data. */
  useEffect(() => {
    if (inventory) {
      const initial: Record<string, number> = {};
      for (const row of inventory) {
        initial[row.tier] = row.totalQuantity;
      }
      // Ensure all tiers have a value (0 if not configured)
      for (const tier of TIERS) {
        if (initial[tier.key] === undefined) {
          initial[tier.key] = 0;
        }
      }
      setQuantities(initial);
      setIsQuantitiesSynced(true);
    }
  }, [inventory]);

  /** True when any tier's quantity is below its reserved + redeemed floor. */
  const hasValidationError = TIERS.some(({ key }) => {
    const row = inventory?.find((i) => i.tier === key);
    const minAllowed = (row?.reservedCount ?? 0) + (row?.redeemedCount ?? 0);
    return (quantities[key] ?? 0) < minAllowed;
  });

  /** Saves inventory quantities by bulk upserting tiers. */
  const handleSave = async () => {
    if (!entryId) {
      return;
    }

    // Send all tiers so setting quantity to 0 is persisted (disables that tier)
    const tiers = TIERS.map(({ key }) => ({
      tier: key,
      totalQuantity: quantities[key] ?? 0,
    }));

    try {
      await upsertInventory.mutateAsync({ catalogEntryId: entryId, tiers });
      enqueueSnackbar('Inventory saved successfully.', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save inventory.', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Inventory — {entryLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the total number of reward slots available per customer tier. Reserved and redeemed counts are
          read-only and reflect live booking state.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Reserved</TableCell>
              <TableCell align="right">Redeemed</TableCell>
              <TableCell align="right">Available</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TIERS.map(({ key, label }) => {
              const row = inventory?.find((i) => i.tier === key);
              const total = quantities[key] ?? 0;
              const reserved = row?.reservedCount ?? 0;
              const redeemed = row?.redeemedCount ?? 0;
              const available = total - reserved - redeemed;
              /** Minimum allowed value: cannot go below slots already in use. */
              const minAllowed = reserved + redeemed;
              const isBelowMin = total < minAllowed;

              return (
                <TableRow key={key}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={total}
                      error={isBelowMin}
                      helperText={isBelowMin ? `Min ${minAllowed} (reserved + redeemed)` : undefined}
                      onChange={(e) =>
                        setQuantities((prev) => ({
                          ...prev,
                          [key]: Math.max(0, parseInt(e.target.value, 10) || 0),
                        }))
                      }
                      inputProps={{ min: minAllowed }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right">{reserved}</TableCell>
                  <TableCell align="right">{redeemed}</TableCell>
                  <TableCell align="right">{available}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={upsertInventory.isPending}
          disabled={hasValidationError || isInventoryFetching || !isQuantitiesSynced}
        >
          Save Inventory
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
